<template>
  <b-modal
    :id="modalId"
    :title="concern ? concern.title : 'Concern Details'"
    size="lg"
    hide-footer
    @hidden="$emit('hidden')"
  >
    <div v-if="!concern" class="text-center py-4 text-muted">No concern selected.</div>

    <div v-else>
      <!-- Header strip -->
      <div class="d-flex align-items-center mb-3">
        <severity-badge :severity="concern.severity" class="mr-2" />
        <span class="text-muted mr-auto">{{ concern.concern_code }}</span>
        <span class="badge" :class="statusClass">{{ concern.status }}</span>
      </div>

      <p class="text-muted mb-3">{{ concern.description }}</p>

      <!-- Scores row -->
      <div class="row text-center mb-3">
        <div class="col-4 border-right">
          <div class="font-weight-bold">{{ scoreLabel(concern.impact_score) }}</div>
          <small class="text-muted">Impact</small>
        </div>
        <div class="col-4 border-right">
          <div class="font-weight-bold">{{ scoreLabel(concern.urgency_score) }}</div>
          <small class="text-muted">Urgency</small>
        </div>
        <div class="col-4">
          <div class="font-weight-bold">{{ scoreLabel(concern.financial_score) }}</div>
          <small class="text-muted">Financial</small>
        </div>
      </div>

      <!-- Context -->
      <div v-if="contextKeys.length" class="mb-3">
        <h6 class="font-weight-semibold">Context</h6>
        <table class="table table-sm table-bordered">
          <tbody>
            <tr v-for="key in contextKeys" :key="key">
              <td class="text-muted" style="width:40%">{{ formatKey(key) }}</td>
              <td class="font-weight-semibold">{{ formatValue(concern.context[key]) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Product link -->
      <div v-if="concern.product" class="mb-3 d-flex align-items-center">
        <i class="bi bi-box-seam mr-2 text-primary"></i>
        <a :href="'/inventory?product=' + concern.product.id" class="text-primary">
          {{ concern.product.product }}
        </a>
      </div>

      <!-- Timeline -->
      <div class="mb-3">
        <h6 class="font-weight-semibold">Timeline</h6>
        <div class="text-muted">
          <div>First seen: {{ formatDate(concern.first_seen_at) }}</div>
          <div>Last seen: {{ formatDate(concern.last_seen_at) }}</div>
          <div v-if="concern.resolved_at">Resolved: {{ formatDate(concern.resolved_at) }}</div>
          <div v-if="concern.snoozed_until">Snoozed until: {{ formatDate(concern.snoozed_until) }}</div>
          <div v-if="concern.recurrence_count > 0">Recurrences: {{ concern.recurrence_count }}</div>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="concern.status === 'ACTIVE'" class="d-flex justify-content-end border-top pt-3">
        <button class="btn btn-outline-secondary btn-sm mr-2" @click="act('acknowledge')" :disabled="acting">
          <b-spinner v-if="acting === 'acknowledge'" small /> Acknowledge
        </button>
        <button class="btn btn-outline-warning btn-sm mr-2" @click="showSnoozeInput = !showSnoozeInput" :disabled="!!acting">
          Snooze
        </button>
        <button class="btn btn-success btn-sm" @click="act('resolve')" :disabled="!!acting">
          <b-spinner v-if="acting === 'resolve'" small /> Mark Resolved
        </button>
      </div>

      <div v-if="showSnoozeInput" class="mt-2 d-flex align-items-center justify-content-end">
        <label class="mb-0 mr-2">Days:</label>
        <input type="number" v-model.number="snoozeDays" min="1" max="90" class="form-control form-control-sm mr-2 snooze-input" />
        <button class="btn btn-warning btn-sm" @click="act('snooze')" :disabled="!!acting">
          <b-spinner v-if="acting === 'snooze'" small /> Confirm Snooze
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import SeverityBadge from './SeverityBadge.vue';

export default {
  name: 'ConcernDetailModal',
  components: { SeverityBadge },
  props: {
    concern:  { type: Object, default: null },
    modalId:  { type: String, default: 'concern-detail-modal' },
  },
  data() {
    return {
      acting: null,
      showSnoozeInput: false,
      snoozeDays: 7,
    };
  },
  computed: {
    statusClass() {
      return {
        ACTIVE:       'badge-success',
        ACKNOWLEDGED: 'badge-info',
        SNOOZED:      'badge-warning',
        RESOLVED:     'badge-secondary',
      }[this.concern?.status] || 'badge-secondary';
    },
    contextKeys() {
      if (!this.concern?.context || typeof this.concern.context !== 'object') return [];
      return Object.keys(this.concern.context);
    },
  },
  methods: {
    scoreLabel(val) {
      if (val == null) return '—';
      return Number(val).toFixed(1);
    },
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    formatValue(val) {
      if (val == null) return '—';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      return String(val);
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleString();
    },
    async act(action) {
      if (!this.concern) return;
      this.acting = action;
      try {
        const url = `/api/intelligence/concerns/${this.concern.id}/${action}`;
        const payload = action === 'snooze' ? { days: this.snoozeDays } : {};
        await axios.patch(url, payload);
        this.$toasts.push({ type: 'success', message: `Concern ${action}d.` });
        this.$emit('updated', { id: this.concern.id, action });
        this.$bvModal.hide(this.modalId);
      } catch (e) {
        this.$toasts.push({ type: 'error', message: e?.response?.data?.message || `Failed to ${action} concern.` });
      } finally {
        this.acting = null;
        this.showSnoozeInput = false;
      }
    },
  },
};
</script>

<style scoped>
.snooze-input {
  width: 80px;
}
</style>
